/**
 * Builds HTML <title> strings for job detail pages: role, optional salary, city, company, brand.
 * Packs the longest variant that fits `maxLength` while keeping the job title untruncated.
 */

/**
 * Google's visible title budget is roughly 60 characters; the same cap the landing pages and
 * SRPs use (see DEFAULT_SEO_TITLE_MAX_LENGTH). Used to be 68 here on the theory that the brand
 * suffix is what gets clipped, but a clipped salary or company reads as a broken snippet.
 */
const DEFAULT_MAX_LENGTH = 60;

function joinParts(parts: string[], separator: string): string {
  return parts.filter(Boolean).join(separator);
}

/**
 * Legal-form suffixes that add nothing to a <title> and cost 3-6 characters that often decide
 * whether the salary fits. Matched case-insensitively at the end of the name, optionally after a
 * comma and with a trailing period ("Acme Oy", "Acme, Oy Ab", "Acme Ltd.").
 */
const COMPANY_LEGAL_SUFFIX_RE =
  /,?\s+(oy\s+ab|ab\s+oy|oyj|oy|ab|abp|ltd\.?|inc\.?|llc|plc|gmbh|a\/s|asa|as|aps|s\.?a\.?|sarl|b\.?v\.?)\.?$/i;

/**
 * "Carrot Palvelut Oy" -> "Carrot Palvelut" for the title only. Searchers type "carrot palvelut
 * työpaikat", not the legal form, and the full registered name stays in the H1, description and
 * schema. Names that would be left too short (or that are only a legal form) are returned as-is.
 */
export function stripCompanyLegalSuffixForTitle(company: string): string {
  const trimmed = company.trim();
  const stripped = trimmed.replace(COMPANY_LEGAL_SUFFIX_RE, '').trim();
  return stripped.length >= 3 ? stripped : trimmed;
}

/**
 * Short location for titles: first locality from a display string (e.g. "Helsinki · Tampere" → "Helsinki").
 */
export function primaryLocalityForJobMetaTitle(locationDisplay: string): string {
  const raw = locationDisplay.trim();
  if (!raw) return '';
  const first = raw.split(/\s*·\s*/)[0]?.trim() ?? raw;
  const beforeComma = first.split(',')[0]?.trim() ?? first;
  if (beforeComma.length > 36) {
    return beforeComma.slice(0, 33).trimEnd() + '…';
  }
  return beforeComma;
}

export type BuildJobListingMetaTitleInput = {
  /** Primary title line (already locale-resolved) */
  title: string;
  company: string;
  /** Short city/region, e.g. "Helsinki" */
  locationFragment: string;
  /** Compact salary range, e.g. "2 500–3 000 €/kk"; omit when unknown. */
  salaryFragment?: string;
  brand?: string;
  maxLength?: number;
};

/**
 * Picks the richest title under the character cap. The job title is never truncated;
 * if it alone exceeds `maxLength`, it is returned as-is (search engines clip in SERPs).
 *
 * Priority when not everything fits, from GSC on jobcrawls.com (Sep 2026):
 *   1. Company stays as long as it fits at all: company-navigational queries
 *      ("{company} työpaikat") are the best-converting segment, and a title that hides the
 *      company loses those clicks ("suomalainen kirjakauppa työpaikat" regression).
 *   2. Salary beats location: "{role} palkka" searches are the single largest query family
 *      landing on job pages (1,000+ query/page pairs in 90 days, near-zero CTR when the snippet
 *      shows no figure), while city queries mostly land on the location hub pages instead.
 *      The old chain only ever tried salary in the everything-fits variant, so at the 60-char
 *      cap "Laatoittaja | Tampere | 1Ketju" won over "Laatoittaja | 2 900–3 600 €/kk | 1Ketju".
 *   3. Location is what's left over.
 */
export function buildJobListingMetaTitle(input: BuildJobListingMetaTitleInput): string {
  const brand = (input.brand ?? 'JobCrawls').trim() || 'JobCrawls';
  const title = (input.title || '').trim();
  const company = stripCompanyLegalSuffixForTitle(input.company || '');
  const loc = (input.locationFragment || '').trim();
  const salary = (input.salaryFragment || '').trim();
  const maxLength = input.maxLength ?? DEFAULT_MAX_LENGTH;

  if (!title) {
    return joinParts([company, brand].filter(Boolean), ' | ') || brand;
  }

  const variants: string[] = [];
  const push = (includeSalary: boolean, includeLoc: boolean, includeCompany: boolean) => {
    const mid: string[] = [];
    if (includeSalary && salary) mid.push(salary);
    if (includeLoc && loc) mid.push(loc);
    if (includeCompany && company) mid.push(company);
    const body = mid.length ? `${title} | ${mid.join(' | ')}` : title;
    const candidate = `${body} | ${brand}`;
    if (!variants.includes(candidate)) variants.push(candidate);
  };

  if (salary) {
    push(true, true, true); // role | salary | city | company
    push(true, false, true); // role | salary | company
    push(false, true, true); // role | city | company
    push(false, false, true); // role | company
    push(true, true, false); // role | salary | city
    push(true, false, false); // role | salary
    push(false, true, false); // role | city
    push(false, false, false); // role
  } else {
    push(false, true, true);
    push(false, true, false);
    push(false, false, true);
    push(false, false, false);
  }

  for (const v of variants) {
    if (v.length <= maxLength) {
      return v;
    }
  }

  if (title.length > maxLength) {
    return title;
  }

  return variants[variants.length - 1] ?? `${title} | ${brand}`;
}
